import { Input } from "@omnitok/ui";
import { useMemo, useState } from "react";

import { useT } from "@/i18n";
import { PROJECT_ICONS } from "@/lib/project-icons";

interface IconPickerProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /** Show the "None" option (default true). */
  allowNone?: boolean;
}

/**
 * Searchable icon picker (PR-1 builder reorg, decisión D9) — used for
 * both project icons and per-report icons (``report_config.icon``).
 * The catalog lives in ``@/lib/project-icons``.
 */
export function IconPicker({
  value,
  onChange,
  disabled = false,
  allowNone = true,
}: IconPickerProps): JSX.Element {
  const { t } = useT();
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => PROJECT_ICONS.find((entry) => entry.name === value),
    [value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PROJECT_ICONS;
    return PROJECT_ICONS.filter((entry) =>
      entry.name.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setQuery(e.target.value)
          }
          placeholder={t("iconPicker.search")}
          disabled={disabled}
          className="max-w-xs flex-1"
        />
        {/* The chosen icon, OUTSIDE the scrolling grid. A search that
            excludes it used to make the selection invisible: nothing on
            screen said which icon the report actually had. */}
        {selected && (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-primary/30 bg-primary-soft px-2 py-1 text-xs font-medium text-primary-ink">
            <selected.component size={14} />
            {selected.name}
          </span>
        )}
      </div>
      {/* Says how many there are, so the scrollbar is not the only clue
          that the box holds more than the four rows it shows. */}
      <p className="text-xs text-neutral-500">
        {query.trim()
          ? t("iconPicker.countFiltered", {
              shown: filtered.length,
              total: PROJECT_ICONS.length,
            })
          : t("iconPicker.count", { total: PROJECT_ICONS.length })}
      </p>
      <div className="flex max-h-56 flex-wrap content-start gap-2 overflow-y-auto pr-1">
        {allowNone && (
          <button
            type="button"
            onClick={() => onChange("")}
            disabled={disabled}
            className={`flex h-9 min-w-[3rem] items-center justify-center rounded-md border px-2 text-xs ${
              value === ""
                ? "border-primary bg-primary-soft text-primary-ink"
                : "border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300"
            }`}
            aria-pressed={value === ""}
          >
            {t("projects.iconNone")}
          </button>
        )}
        {filtered.map(({ name, component: Icon }) => (
          <button
            key={name}
            type="button"
            onClick={() => onChange(name)}
            disabled={disabled}
            title={name}
            aria-label={name}
            aria-pressed={value === name}
            className={`flex h-9 w-9 items-center justify-center rounded-md border ${
              value === name
                ? "border-primary bg-primary-soft text-primary-ink"
                : "border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300"
            }`}
          >
            <Icon size={16} />
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="py-2 text-xs text-neutral-500">
            {t("iconPicker.noResults")}
          </p>
        )}
      </div>
    </div>
  );
}
